import { z } from 'zod';
import { INTERNAL_ONLY, type Visibility } from '../../domain/entities/common.ts';
import type { Assignee, Reminder, ScheduleItem } from '../../domain/entities/schedule.ts';
import type { RecordRef } from '../../domain/entities/notification.ts';
import {
  assigneeFields,
  DueSchema,
  TodoSchema,
  type Due,
  type Todo,
  type TodoPriority,
} from '../../domain/entities/todo.ts';
import { linkKey } from '../../domain/entities/file.ts';
import { addWorkingDays, nextWorkingDay, type WorkCalendar } from '../../domain/work-calendar.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Writing a to-do.
 *
 * The thing that makes this more than a list is the due date: it can follow
 * a schedule item rather than being a date somebody typed, so a schedule
 * that moves carries its to-dos with it. Everything else here exists to
 * keep that honest.
 */
export class TodoError extends Error {
  readonly reason: 'invalid' | 'notYours' | 'noItem';

  constructor(reason: 'invalid' | 'notYours' | 'noItem', message: string) {
    super(message);
    this.name = 'TodoError';
    this.reason = reason;
  }
}

export const SaveTodoInputSchema = z.object({
  title: z.string().trim().min(1, 'A to-do needs a title').max(200),
  notes: z.string().max(20_000).optional(),
  assignees: z.array(z.custom<Assignee>()).optional(),
  due: DueSchema.nullish(),
  priority: z.custom<TodoPriority>().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
  reminder: z.custom<Reminder>().optional(),
  visibility: z.custom<Visibility>().optional(),
  linkedRecords: z.array(z.custom<RecordRef>()).optional(),
});
export type SaveTodoInput = z.input<typeof SaveTodoInputSchema>;

export interface TodoContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

function repo(context: Pick<TodoContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<Todo>(
    { companyId: context.companyId, jobId: context.jobId },
    'todos',
  );
}

function linkFields(records: readonly RecordRef[]) {
  return { linkedRecords: [...records], linkedKeys: records.map(linkKey) };
}

export async function createTodo(context: TodoContext, input: SaveTodoInput): Promise<Todo> {
  const parsed = SaveTodoInputSchema.safeParse(input);
  if (!parsed.success) throw new TodoError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const todo: Todo = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    notes: parsed.data.notes ?? '',
    ...assigneeFields(parsed.data.assignees ?? []),
    due: parsed.data.due ?? undefined,
    priority: parsed.data.priority ?? 'medium',
    tagIds: parsed.data.tagIds ?? [],
    reminder: parsed.data.reminder ?? 'none',
    visibility: parsed.data.visibility ?? INTERNAL_ONLY,
    completed: false,
    checklist: [],
    ...linkFields(parsed.data.linkedRecords ?? []),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = TodoSchema.safeParse(todo);
  if (!checked.success) throw new TodoError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(todo);
  return todo;
}

/**
 * Change a to-do.
 *
 * Guarded with the version it read. Passing `due: null` clears the date
 * where leaving it out keeps it, which is the distinction that lets "remove
 * the due date" and "do not touch the due date" both be expressible.
 */
export async function updateTodo(
  context: TodoContext,
  id: string,
  input: SaveTodoInput,
): Promise<Todo> {
  const parsed = SaveTodoInputSchema.safeParse(input);
  if (!parsed.success) throw new TodoError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('todos', id);

  if (parsed.data.visibility?.private && existing.createdBy !== context.uid) {
    throw new TodoError(
      'notYours',
      'That to-do was written by somebody else, so it cannot be made private',
    );
  }

  const next: Todo = {
    ...existing,
    title: parsed.data.title,
    notes: parsed.data.notes ?? existing.notes,
    ...assigneeFields(parsed.data.assignees ?? existing.assignees),
    due: parsed.data.due === undefined ? existing.due : (parsed.data.due ?? undefined),
    priority: parsed.data.priority ?? existing.priority,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    reminder: parsed.data.reminder ?? existing.reminder,
    visibility: parsed.data.visibility ?? existing.visibility,
    ...linkFields(parsed.data.linkedRecords ?? existing.linkedRecords),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = TodoSchema.safeParse(next);
  if (!checked.success) throw new TodoError('invalid', checked.error.issues[0]!.message);

  const { id: _id, ...patch } = next;
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'todos',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return next;
}

/**
 * Mark done, or not done.
 *
 * Who and when are recorded, because "it was ticked" and "Mike ticked it on
 * Tuesday" are different answers to the same complaint. Un-completing
 * clears both rather than leaving a stale pair that says the to-do was
 * finished by somebody at a time when it was not.
 */
export async function setTodoComplete(
  context: TodoContext,
  id: string,
  complete: boolean,
): Promise<Todo> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('todos', id);
  // Ticking a ticked to-do keeps the first answer: a double click is one
  // act, and "when was this done" must not move because somebody clicked
  // again.
  if (existing.completed === complete) return existing;

  const now = context.clock.now().toISOString();
  const patch = complete
    ? {
        completed: true,
        completedAt: now,
        completedBy: context.uid,
        updatedAt: now,
        updatedBy: context.uid,
      }
    : {
        completed: false,
        completedAt: undefined,
        completedBy: undefined,
        updatedAt: now,
        updatedBy: context.uid,
      };

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'todos',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch } as Todo;
}

export async function deleteTodo(context: TodoContext, id: string): Promise<void> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('todos', id);
  await repo(context).delete(id);
}

// ---- due dates -----------------------------------------------------------

/**
 * The day a to-do is actually due.
 *
 * A typed date is itself. A schedule-linked date is the item's start or end,
 * offset by working days — so "two days before framing starts" moves when
 * framing does, which is the whole point of the link.
 *
 * Returns null when the link points at an item that is not there. A to-do
 * whose schedule item was deleted has no due date rather than a wrong one,
 * and the UI says the link is broken instead of inventing a day.
 */
export function resolveDue(
  due: Due | undefined,
  calendar: WorkCalendar,
  items: ReadonlyMap<string, Pick<ScheduleItem, 'startDate' | 'endDate'>>,
): string | null {
  if (!due) return null;
  if (due.kind === 'date') return due.date;

  const item = items.get(due.itemId);
  if (!item) return null;

  const anchor = due.anchor === 'start' ? item.startDate : item.endDate;
  // Normalised first: a stored date could be a non-working day, and
  // counting from one would make every offset off by a day.
  const from = nextWorkingDay(calendar, anchor);
  return due.offsetDays === 0 ? from : addWorkingDays(calendar, from, due.offsetDays);
}

/** Whether a link points at an item that no longer exists. */
export function dueLinkIsBroken(
  due: Due | undefined,
  items: ReadonlyMap<string, unknown>,
): boolean {
  return due?.kind === 'scheduleItem' && !items.has(due.itemId);
}

// ---- reading -------------------------------------------------------------

/**
 * The two queries whose union is "the to-dos on this job you may see".
 *
 * The same shape and reason as `visibleLogQueries`: rules are not filters.
 */
export function visibleTodoQueries(uid: string): readonly Query[] {
  const order = [{ field: 'createdAt', direction: 'desc' as const }];
  return [
    { where: [{ field: 'visibility.private', op: '==', value: false }], orderBy: order },
    {
      where: [
        { field: 'visibility.private', op: '==', value: true },
        { field: 'createdBy', op: '==', value: uid },
      ],
      orderBy: order,
    },
  ];
}

export function mergeTodos(...pages: readonly (readonly Todo[])[]): Todo[] {
  const byId = new Map<string, Todo>();
  for (const page of pages) {
    for (const todo of page) byId.set(todo.id, todo);
  }
  return [...byId.values()].sort(
    (a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id),
  );
}

export async function listTodos(
  context: Pick<TodoContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
): Promise<Todo[]> {
  const pages = await Promise.all(
    visibleTodoQueries(context.uid).map((query) => repo(context).list(query)),
  );
  return mergeTodos(...pages.map((page) => page.items));
}

/** The same, live. Nothing is reported until both queries have answered. */
export function subscribeToTodos(
  context: Pick<TodoContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  onChange: (todos: readonly Todo[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  const queries = visibleTodoQueries(context.uid);
  const pages: (readonly Todo[] | undefined)[] = queries.map(() => undefined);

  const emit = () => {
    const answered: (readonly Todo[])[] = [];
    for (const page of pages) {
      if (page === undefined) return;
      answered.push(page);
    }
    onChange(mergeTodos(...answered));
  };

  const stops = queries.map((query, index) =>
    repo(context).subscribe(
      query,
      (page) => {
        pages[index] = page.items;
        emit();
      },
      onError,
    ),
  );

  return () => {
    for (const stop of stops) stop();
  };
}
